import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import logoUnotif from "@/assets/logo-unotif.png";
import faviconUnotif from "@/assets/favicon-unotif.png";

const useCasesItems = [
  { label: "Créateurs & influenceurs", href: "/cas-utilisation/createurs-influenceurs" },
  { label: "Coachs & communautés", href: "/cas-utilisation/coachs-communautes" },
  { label: "Conférenciers & événements", href: "/cas-utilisation/conferenciers-evenements" },
  { label: "Commerces locaux", href: "/cas-utilisation/commerces-locaux" },
  { label: "Agences marketing", href: "/cas-utilisation/agences" },
];

const navLinks = [
  { label: "Comment ça marche", href: "/#comment-ca-marche" },
  { label: "Fonctionnalités", href: "/#fonctionnalites" },
  { label: "Tarifs", href: "/tarifs" },
  { label: "FAQ", href: "/#faq" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-md border-b border-border"
          : "bg-background"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - eager loading pour above-the-fold */}
          <Link to="/" className="flex items-center">
            {/* Desktop logo (lg+) */}
            <img
              src={logoUnotif}
              alt="UNOTIF"
              className="h-10 hidden lg:block"
              width={144}
              height={40}
              loading="eager"
              decoding="async"
            />
            {/* Tablet logo (md to lg) */}
            <img
              src={logoUnotif}
              alt="UNOTIF"
              className="h-8 hidden md:block lg:hidden"
              width={115}
              height={32}
              loading="eager"
              decoding="async"
            />
            <img
              src={faviconUnotif}
              alt="UNOTIF"
              className="h-10 w-10 md:hidden"
              width={40}
              height={40}
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Hover Dropdown: À qui s'adresse */}
            <div
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <span className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-[#074155] hover:text-foreground transition-colors rounded-lg hover:bg-accent cursor-default">
                À qui s'adresse
                <ChevronDown className="w-4 h-4" />
              </span>
              {dropdownOpen && (
                <div className="absolute top-full left-0 w-56 bg-popover border border-border shadow-lg rounded-md z-50 pt-2 pb-1">
                  {useCasesItems.map((item, index) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`block px-3 py-2 text-sm font-medium hover:bg-accent transition-colors ${
                        index < 4 ? "text-[#074155]" : "text-foreground"
                      }`}
                      onClick={() => setDropdownOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}

            </div>

            {/* Other nav links */}
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="px-3 py-2 text-sm font-medium text-[#074155] hover:text-foreground transition-colors rounded-lg hover:bg-accent"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              asChild
              className="border-primary/30 text-primary hover:bg-primary/5 hover:border-primary font-medium"
            >
              <a href="https://app.unotif.com/auth?mode=signin">Se connecter</a>
            </Button>
            <Button asChild className="bg-primary hover:bg-[hsl(187,87%,32%)] shadow-md font-medium">
              <a href="https://app.unotif.com/auth?mode=signup">
                Commencer gratuitement
              </a>
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 bg-background">
              <div className="flex flex-col h-full">
                <div className="flex items-center mb-8">
                  <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                    <img src={logoUnotif} alt="UNOTIF" className="h-8" width={115} height={32} />
                  </Link>
                </div>

                <nav className="flex flex-col gap-2">
                  {/* Mobile: À qui s'adresse */}
                  <div className="py-2">
                    <p className="px-3 text-sm font-medium text-[#074155] mb-2">
                      À qui s'adresse
                    </p>
                    <div className="flex flex-col gap-1 pl-3">
                      {useCasesItems.map((item, index) => (
                        <SheetClose key={item.href} asChild>
                          <Link
                            to={item.href}
                            className={`px-3 py-2 text-sm font-medium hover:text-foreground transition-colors rounded-lg hover:bg-accent ${
                              index < 4 ? "text-[#074155]" : "text-foreground"
                            }`}
                          >
                            {item.label}
                          </Link>
                        </SheetClose>
                      ))}
                    </div>
                  </div>

                  <div className="h-px bg-border my-2" />

                  {/* Mobile: Other links */}
                  {navLinks.map((link) => (
                    <SheetClose key={link.href} asChild>
                      <Link
                        to={link.href}
                        className="px-3 py-2 text-sm font-medium text-[#074155] hover:text-foreground transition-colors rounded-lg hover:bg-accent"
                      >
                        {link.label}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>

                {/* Mobile CTAs */}
                <div className="mt-auto flex flex-col gap-3 pt-6">
                  <Button
                    variant="outline"
                    asChild
                    className="w-full border-primary/30 text-primary hover:bg-primary/5 font-medium"
                  >
                    <a href="https://app.unotif.com/auth?mode=signin">
                      Se connecter
                    </a>
                  </Button>
                  <Button asChild className="w-full bg-primary hover:bg-[hsl(187,87%,32%)] font-medium">
                    <a href="https://app.unotif.com/auth?mode=signup">
                      Commencer gratuitement
                    </a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
